import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../api";
import { t, Locale } from "../../i18n";

interface TwoFASetupResponse {
  secret: string;
  qrCodeUri: string;
}

interface TwoFAVerifySetupResponse {
  backup_codes: string[];
  message: string;
}

interface TwoFAStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
}

type SetupState = "idle" | "setup" | "verify" | "enabled" | "show-codes" | "disable-confirm" | "regenerate-confirm";

export function TwoFactorAuth(props: { locale: Locale }) {
  const locale = props.locale;
  const [state, setState] = useState<SetupState>("idle");
  const [status, setStatus] = useState<TwoFAStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disableCode, setDisableCode] = useState("");
  const [regenerateCode, setRegenerateCode] = useState("");
  const [codesSaved, setCodesSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load 2FA status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    const result = await apiGet<TwoFAStatusResponse>("/v1/2fa/status");
    if (result.ok) {
      setStatus(result.data);
      setState(result.data.enabled ? "enabled" : "idle");
    } else {
      setError(result.error);
    }
  };

  const handleStartSetup = async () => {
    setLoading(true);
    setError(null);
    // Backend route is POST /v1/2fa/setup; handleSetup reads no body, empty {} is fine.
    const result = await apiPost<TwoFASetupResponse>("/v1/2fa/setup", {});
    setLoading(false);

    if (result.ok) {
      setQrCode(result.data.qrCodeUri);
      setSecret(result.data.secret);
      setState("setup");
    } else {
      setError(result.error);
    }
  };

  const handleVerifySetup = async () => {
    if (!totpCode || totpCode.length < 6) {
      setError(t(locale, "twofa.error.invalidTotp"));
      return;
    }
    if (!secret) {
      // Defensive: the setup step always populates secret before we reach this state.
      setError(t(locale, "twofa.error.sessionExpired"));
      setState("idle");
      return;
    }

    setLoading(true);
    setError(null);
    // Backend handleVerifySetup expects { secret, code } — verifyAndEnable needs both.
    const result = await apiPost<TwoFAVerifySetupResponse>("/v1/2fa/verify-setup", {
      secret,
      code: totpCode
    });
    setLoading(false);

    if (result.ok) {
      setBackupCodes(result.data.backup_codes);
      setCodesSaved(false);
      setCopied(false);
      setState("show-codes");
      // Set the count optimistically from the codes we just received so the
      // "enabled" view doesn't flash a stale 0 before the next status fetch.
      // (A direct set, not {...prev}, because enrollment always yields a known
      // enabled+count state even if prev was null.)
      setStatus({ enabled: true, backup_codes_remaining: result.data.backup_codes.length });
      setTotpCode("");
    } else {
      setError(result.error);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length < 6) {
      setError(t(locale, "twofa.error.invalidCode"));
      return;
    }

    setLoading(true);
    setError(null);
    const result = await apiPost("/v1/2fa/disable", {
      code: disableCode
    });
    setLoading(false);

    if (result.ok) {
      setStatus((prev) => prev ? { ...prev, enabled: false } : null);
      setState("idle");
      setDisableCode("");
    } else {
      setError(result.error);
    }
  };

  const handleStartRegenerate = () => {
    setError(null);
    setRegenerateCode("");
    setState("regenerate-confirm");
  };

  const handleConfirmRegenerate = async () => {
    if (!regenerateCode || regenerateCode.length < 6) {
      setError(t(locale, "twofa.error.invalidTotp"));
      return;
    }

    setLoading(true);
    setError(null);
    const result = await apiPost<TwoFAVerifySetupResponse>("/v1/2fa/regenerate-backup-codes", {
      code: regenerateCode
    });
    setLoading(false);

    if (result.ok) {
      setBackupCodes(result.data.backup_codes);
      setCodesSaved(false);
      setCopied(false);
      setRegenerateCode("");
      setState("show-codes");
      // Refresh the remaining-codes count so returning to "enabled" reflects
      // the freshly regenerated set, not the depleted prior count.
      setStatus((prev) => prev
        ? { ...prev, backup_codes_remaining: result.data.backup_codes.length }
        : { enabled: true, backup_codes_remaining: result.data.backup_codes.length });
    } else {
      setError(result.error);
    }
  };

  const handleCopyCodes = async () => {
    const text = backupCodes.join("\n");
    // Clear any prior failure so a successful retry doesn't leave a stale
    // "failed to copy" message behind.
    setError(null);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for insecure origins (e.g. the plain-HTTP dev host) where
        // navigator.clipboard is undefined. execCommand is deprecated but is
        // the only clipboard path available outside a secure context.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand copy returned false");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t(locale, "twofa.error.copyFailed"));
    }
  };

  return (
    <div style={{ background: "var(--canvas)", border: "1px solid var(--line)", borderRadius: "8px", padding: "20px" }}>
      <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "twofa.title")}</h3>

      {error && (
        <div style={{
          background: "rgba(239, 68, 68, 0.1)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          borderRadius: "6px",
          padding: "12px",
          marginBottom: "16px",
          color: "#ef4444",
          fontSize: "13px"
        }}>
          {error}
        </div>
      )}

      {state === "idle" && (
        <div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "16px" }}>
            {t(locale, "twofa.description")}
          </p>
          <button
            onClick={handleStartSetup}
            disabled={loading}
            style={{
              background: loading ? "var(--text-secondary)" : "var(--accent)",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: 600,
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.opacity = "1")}
          >
            {loading ? t(locale, "twofa.settingUp") : t(locale, "twofa.enable")}
          </button>
        </div>
      )}

      {state === "setup" && (
        <div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "12px" }}>
            {t(locale, "twofa.setup.scanQr")}
          </p>
          {qrCode && (
            <div style={{ marginBottom: "16px" }}>
              <img src={qrCode} alt={t(locale, "twofa.setup.qrAlt")} style={{ maxWidth: "200px", border: "1px solid var(--line)", padding: "8px" }} />
            </div>
          )}
          <details style={{ marginBottom: "16px" }}>
            <summary style={{ cursor: "pointer", color: "var(--muted)", fontSize: "12px" }}>{t(locale, "twofa.setup.cantScan")}</summary>
            {secret && (
              <div style={{ marginTop: "8px", fontFamily: "monospace", fontSize: "12px", background: "var(--bg)", padding: "8px", borderRadius: "4px", wordBreak: "break-all" }}>
                {secret}
              </div>
            )}
          </details>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>
              {t(locale, "twofa.setup.enterCode")}
            </label>
            <input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "monospace",
                letterSpacing: "8px",
                textAlign: "center"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleVerifySetup}
              disabled={loading || totpCode.length !== 6}
              style={{
                background: "var(--primary)",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: totpCode.length === 6 && !loading ? "pointer" : "not-allowed",
                fontSize: "13px",
                opacity: totpCode.length === 6 && !loading ? 1 : 0.6
              }}
            >
              {loading ? t(locale, "twofa.verifying") : t(locale, "twofa.verifyAndEnable")}
            </button>
            <button
              onClick={() => setState("idle")}
              disabled={loading}
              style={{
                background: "transparent",
                border: "1px solid var(--control-border)",
                color: "var(--text)",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {state === "show-codes" && (
        <div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "12px" }}>
            {t(locale, "twofa.showCodes.description")}
          </p>
          <div style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            fontFamily: "monospace",
            fontSize: "13px",
            lineHeight: "1.8"
          }}>
            {backupCodes.map((code, i) => (
              <div key={i}>{code}</div>
            ))}
          </div>

          {/* App-password transition notice — IMAP/SMTP clients break until an app password is minted */}
          <div style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "var(--muted)",
            fontSize: "12px",
            lineHeight: "1.5"
          }}>
            {t(locale, "twofa.appPasswordNotice")}
          </div>

          <label style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            fontSize: "13px",
            color: "var(--text)",
            cursor: "pointer"
          }}>
            <input
              type="checkbox"
              checked={codesSaved}
              onChange={(e) => setCodesSaved(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            {t(locale, "twofa.codesSavedCheck")}
          </label>

          <button
            onClick={handleCopyCodes}
            style={{
              background: "transparent",
              border: "1px solid var(--control-border)",
              color: copied ? "var(--success)" : "var(--text)",
              padding: "8px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              marginRight: "8px"
            }}
          >
            {copied ? t(locale, "twofa.copied") : t(locale, "twofa.copyToClipboard")}
          </button>
          <button
            onClick={() => { setError(null); setState("enabled"); }}
            disabled={!codesSaved}
            style={{
              background: "var(--primary)",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "6px",
              cursor: codesSaved ? "pointer" : "not-allowed",
              fontSize: "13px",
              opacity: codesSaved ? 1 : 0.6
            }}
          >
            {t(locale, "common.done")}
          </button>
        </div>
      )}

      {state === "enabled" && (
        <div>
          <div style={{ marginBottom: "16px", padding: "12px", background: "rgba(34, 197, 94, 0.1)", borderRadius: "6px", color: "var(--success)" }}>
            {t(locale, "twofa.enabled")}
          </div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "16px" }}>
            {t(locale, "twofa.remainingCodes", { count: String(status?.backup_codes_remaining || 0) })}
          </p>

          {/* App-password transition notice — IMAP/SMTP clients break until an app password is minted */}
          <div style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "var(--muted)",
            fontSize: "12px",
            lineHeight: "1.5"
          }}>
            {t(locale, "twofa.appPasswordNotice")}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              onClick={handleStartRegenerate}
              disabled={loading}
              style={{
                background: "transparent",
                border: "1px solid var(--control-border)",
                color: "var(--text)",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "twofa.regenerateCodes")}
            </button>
            <button
              onClick={() => setState("disable-confirm")}
              style={{
                background: "transparent",
                border: "1px solid var(--control-border)",
                color: "var(--danger)",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "twofa.disable")}
            </button>
          </div>
        </div>
      )}

      {state === "disable-confirm" && (
        <div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "12px" }}>
            {t(locale, "twofa.disableConfirm.description")}
          </p>
          <div style={{ marginBottom: "12px" }}>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder={t(locale, "twofa.disableConfirm.placeholder")}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDisable}
              disabled={loading}
              style={{
                background: "var(--danger)",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "13px",
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? t(locale, "twofa.disabling") : t(locale, "twofa.disable")}
            </button>
            <button
              onClick={() => {
                setState("enabled");
                setDisableCode("");
              }}
              disabled={loading}
              style={{
                background: "transparent",
                border: "1px solid var(--control-border)",
                color: "var(--text)",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {state === "regenerate-confirm" && (
        <div>
          <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "12px" }}>
            {t(locale, "twofa.regenerateConfirm.description")}
          </p>
          <div style={{ marginBottom: "12px" }}>
            <input
              type="text"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "monospace",
                letterSpacing: "8px",
                textAlign: "center"
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleConfirmRegenerate}
              disabled={loading || regenerateCode.length !== 6}
              style={{
                background: "var(--primary)",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: regenerateCode.length === 6 && !loading ? "pointer" : "not-allowed",
                fontSize: "13px",
                opacity: regenerateCode.length === 6 && !loading ? 1 : 0.6
              }}
            >
              {loading ? t(locale, "twofa.regenerating") : t(locale, "twofa.regenerate")}
            </button>
            <button
              onClick={() => {
                setState("enabled");
                setRegenerateCode("");
                setError(null);
              }}
              disabled={loading}
              style={{
                background: "transparent",
                border: "1px solid var(--control-border)",
                color: "var(--text)",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
